import { useSyncExternalStore } from 'react';

/**
 * Has a remote app mounted at least once in this document?
 *
 * It decides WHICH loader a remote's chunk load shows. The first one is a cold
 * boot — nothing is on screen yet, so the full-bleed brand splash
 * ({@link AppBootSplash}) is the right answer. Every load after it happens with
 * the chrome already painted: rail, sidebar panel and top bar are all sitting
 * there, and dropping the same brand splash into the content well reads as the
 * whole product restarting rather than one panel filling in. Those get the
 * app-level skeleton instead — the same `PageLoader` each app already uses for
 * its own route transitions, so a remote hand-off and an in-app navigation look
 * like the same event.
 *
 * A module-level store rather than context or a prop: the flag is per-document,
 * is written by a routed child, and is read by that same subtree on later
 * navigations — there is no common provider worth threading it through. It
 * deliberately resets on reload, which is exactly when a cold boot happens again.
 *
 * Same shape as {@link appOutageStore} so there is one store idiom in the shell.
 */
let booted = false;
const listeners = new Set<() => void>();

/** Called once a remote has actually rendered. Idempotent. */
export function markShellBooted(): void {
  if (booted) return;
  booted = true;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return booted;
}

/** True once any remote app has mounted in this document. */
export function useShellBooted(): boolean {
  // Server snapshot is always false — nothing has booted before hydration.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
